import { motionAPI } from "@blings/blings-player/editor-tools";
import { updateObjectValues } from "../../../helpers/JsonSearch";
import {
  getKeyframePropertyPath,
  normalizeKeyframePath,
  type KeyframeInfo,
} from "./keyframeHelpers";
import {
  getLayerData,
  type LayerKeyframeData,
  type TimelineLayerInfo,
} from "./layerHierarchy";
import { normalizeAssetId } from "./timingModHelpers";
import {
  getValueAtObjectPath,
  removeKeyframeFromAnimatedPropertyValue,
} from "./rootAnimationModHelpers";
import { buildTimelineKeyframeId } from "./keyframeSelectionHelpers";

type TimelineLayerScanEntry = {
  layer: TimelineLayerInfo;
};

export type TimelineKeyframeDeletionTarget = {
  id: string;
  layer: TimelineLayerInfo;
  keyframe: KeyframeInfo;
};

function normalizeEditableKeyframeValue(value: unknown): number[] | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return [value];
  }

  if (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => typeof entry === "number" && Number.isFinite(entry))
  ) {
    return value;
  }

  return null;
}

function getTimelineMotionTarget(layer: TimelineLayerInfo): {
  assetId: string | undefined;
  layerName: string;
  layerUid: number | undefined;
} {
  return {
    assetId: normalizeAssetId(layer.assetId),
    layerName: layer.nm,
    layerUid: layer.uid,
  };
}

function shouldUseMotionKeyframeRemoval(keyframe: KeyframeInfo): boolean {
  return normalizeEditableKeyframeValue(keyframe.originalKeyframe.s) !== null;
}

function removeKeyframeWithJsonFallback(
  workingAnimation: unknown,
  target: TimelineKeyframeDeletionTarget
): boolean {
  const workingLayerData = getLayerData(
    workingAnimation,
    target.layer.layerPath
  );
  const propertyPath = getKeyframePropertyPath(target.keyframe.path);
  const currentValue = getValueAtObjectPath(workingLayerData, propertyPath);
  const nextValue = removeKeyframeFromAnimatedPropertyValue(
    currentValue,
    target.keyframe.t
  );

  if (!nextValue.removed) {
    return false;
  }

  updateObjectValues(
    workingLayerData as Record<string, unknown>,
    [propertyPath],
    [nextValue.value as any]
  );

  return true;
}

function removeKeyframeWithMotionTools(
  workingAnimation: unknown,
  target: TimelineKeyframeDeletionTarget
): boolean {
  const applied = motionAPI.apply.removePropertyKeyframe(
    workingAnimation as any,
    getTimelineMotionTarget(target.layer),
    getKeyframePropertyPath(target.keyframe.path),
    target.keyframe.t
  );

  return applied.changes.length > 0;
}

export function collectSelectedKeyframeDeletionTargets(
  layers: TimelineLayerScanEntry[],
  layerKeyframeData: Map<string, LayerKeyframeData>,
  selectedKeyframeIds: Set<string>
): TimelineKeyframeDeletionTarget[] {
  if (!selectedKeyframeIds.size) {
    return [];
  }

  const targets: TimelineKeyframeDeletionTarget[] = [];

  layers.forEach(({ layer }) => {
    const keyframeData = layerKeyframeData.get(layer.nodeKey);
    if (!keyframeData) {
      return;
    }

    keyframeData.rows.forEach((row) => {
      row.keyframes.forEach((keyframe) => {
        const id = buildTimelineKeyframeId(layer, keyframe);
        if (!selectedKeyframeIds.has(id)) {
          return;
        }

        targets.push({
          id,
          layer,
          keyframe: {
            ...keyframe,
            path: normalizeKeyframePath(keyframe.path),
          },
        });
      });
    });
  });

  return targets;
}

export function applyKeyframeDeletionTargets(
  workingAnimation: unknown,
  targets: TimelineKeyframeDeletionTarget[]
): boolean {
  let changed = false;

  targets.forEach((target) => {
    const didRemove = shouldUseMotionKeyframeRemoval(target.keyframe)
      ? removeKeyframeWithMotionTools(workingAnimation, target)
      : removeKeyframeWithJsonFallback(workingAnimation, target);

    changed = changed || didRemove;
  });

  return changed;
}
